import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as CheckCircleIcon } from '../../../icons/check-circle.svg';

interface FreeTextGuidanceBoxProps {
  /** Current saved value — null means empty/cleared. */
  value: string | null;
  /** Called when the user saves. Empty trimmed input is passed as null. */
  onSave: (value: string | null) => void;
  /** Label shown above the editor and next to the tick once saved. */
  label: string;
  /** Placeholder shown inside the textarea while editing. */
  editPlaceholder: string;
  /** Clickable example chips that populate the editor. */
  exampleChips: string[];
  /** Label of the primary button while editing, e.g. "Save alerts". */
  saveLabel: string;
  /** Label of the destructive button once saved, e.g. "Clear alerts". */
  clearLabel: string;
  /**
   * Rendered instead of the editor when nothing is saved and the user has not
   * started editing — the collapsed trigger or a full empty state.
   */
  renderEmpty: (startEditing: () => void) => React.ReactNode;
  /** Test id prefix for QA hooks. */
  testId?: string;
}

const FreeTextGuidanceBox: React.FC<FreeTextGuidanceBoxProps> = ({
  value,
  onSave,
  label,
  editPlaceholder,
  exampleChips,
  saveLabel,
  clearLabel,
  renderEmpty,
  testId = 'free-text',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const savedValue = value?.trim() || null;

  const startEditing = (initial = '') => {
    setDraft(initial);
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmed = draft.trim();
    onSave(trimmed ? trimmed : null);
    setIsEditing(false);
    setDraft('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setDraft('');
  };

  const appendExample = (chip: string) => {
    const sentence = `${chip}.`;
    setDraft((prev) => (prev.trim() ? `${prev.trim()} ${sentence}` : sentence));
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className='guidance is-editing' data-testid={testId}>
        <span className='guidance-label' data-testid={`${testId}-label`}>
          {label}
        </span>
        <textarea
          ref={textareaRef}
          className='notes-input'
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={editPlaceholder}
          rows={4}
          data-testid={`${testId}-textarea`}
        />
        <div className='chip-row'>
          {exampleChips.map((chip) => (
            <button
              key={chip}
              type='button'
              className='chip'
              onClick={() => appendExample(chip)}
              data-testid={`${testId}-chip-${chip}`}
            >
              {chip}
            </button>
          ))}
        </div>
        <div className='guidance-foot'>
          <button
            type='button'
            className='btn btn-sm btn-dark'
            onClick={handleSave}
            data-testid={`${testId}-save`}
          >
            {saveLabel}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-plain'
            onClick={handleCancel}
            data-testid={`${testId}-cancel`}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (savedValue) {
    return (
      <div className='guidance is-saved' data-testid={testId}>
        <span className='guidance-label' data-testid={`${testId}-label`}>
          <CheckCircleIcon className='guidance-check' />
          {label}
        </span>
        <p className='guidance-text' data-testid={`${testId}-value`}>
          {savedValue}
        </p>
        <div className='guidance-foot'>
          <button
            type='button'
            className='btn btn-sm'
            onClick={() => startEditing(savedValue)}
            data-testid={`${testId}-edit`}
          >
            Edit
          </button>
          <button
            type='button'
            className='btn btn-sm btn-plain'
            onClick={() => onSave(null)}
            data-testid={`${testId}-clear`}
          >
            {clearLabel}
          </button>
        </div>
      </div>
    );
  }

  return <>{renderEmpty(() => startEditing(''))}</>;
};

export default FreeTextGuidanceBox;
